import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import {
  normalizeSupportTicketReporterLocale,
  SupportTicketMessageModel,
  SupportTicketModel,
  SupportTicketStatus,
} from '@nx-c4g/c4g-models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { notify } from '../notify-drawer/notify';
import { SupportTicketService } from './support-ticket.service';

export interface SupportTicketDetailDialogData {
  ticket: SupportTicketModel;
}

@Component({
  selector: 'nx-c4g-support-ticket-detail-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    TranslateModule,
  ],
  templateUrl: './support-ticket-detail-dialog.component.html',
  styleUrls: ['./support-ticket-detail-dialog.component.scss'],
})
export class SupportTicketDetailDialogComponent implements OnInit {
  readonly ticket: SupportTicketModel;

  messageThread: SupportTicketMessageModel[] = [];
  messagesLoading = false;
  newCommentBody = '';
  postingComment = false;
  statusActionLoading = false;
  private closed = false;

  constructor(
    private readonly dialogRef: MatDialogRef<SupportTicketDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: SupportTicketDetailDialogData,
    private readonly supportTicketService: SupportTicketService,
    private readonly translate: TranslateService
  ) {
    this.ticket = data.ticket;
  }

  ngOnInit(): void {
    void this.loadThread();
  }

  close(): void {
    this.closed = true;
    this.dialogRef.close();
  }

  messageAuthorLabel(m: SupportTicketMessageModel): string {
    return m.author === 'user'
      ? this.translate.instant('supportTickets.messageFromYou')
      : this.translate.instant('supportTickets.messageFromSupport');
  }

  canMarkResolved(): boolean {
    return (
      this.ticket.status === 'open' || this.ticket.status === 'in_progress'
    );
  }

  canReopenTicket(): boolean {
    return this.ticket.status === 'resolved';
  }

  async markTicketResolved(): Promise<void> {
    if (this.statusActionLoading || !this.canMarkResolved()) {
      return;
    }
    this.statusActionLoading = true;
    try {
      const updated = await this.supportTicketService.updateStatus(
        this.ticket._id,
        'resolved'
      );
      Object.assign(this.ticket, updated);
      notify(
        this.translate.instant('supportTickets.toast.markedResolved'),
        'info',
        2500
      );
    } catch {
      notify(
        this.translate.instant('supportTickets.toast.statusUpdateFailed'),
        'error',
        4000
      );
    } finally {
      this.statusActionLoading = false;
    }
  }

  async reopenTicket(): Promise<void> {
    if (this.statusActionLoading || !this.canReopenTicket()) {
      return;
    }
    this.statusActionLoading = true;
    try {
      const updated = await this.supportTicketService.updateStatus(
        this.ticket._id,
        'open'
      );
      Object.assign(this.ticket, updated);
      notify(
        this.translate.instant('supportTickets.toast.ticketReopened'),
        'info',
        2500
      );
    } catch {
      notify(
        this.translate.instant('supportTickets.toast.statusUpdateFailed'),
        'error',
        4000
      );
    } finally {
      this.statusActionLoading = false;
    }
  }

  async submitComment(): Promise<void> {
    const body = this.newCommentBody.trim();
    if (!body || this.postingComment) {
      return;
    }
    this.postingComment = true;
    try {
      await this.supportTicketService.addMessage(this.ticket._id, {
        body,
        reporterLocale: normalizeSupportTicketReporterLocale(
          this.translate.currentLang
        ),
      });
      this.newCommentBody = '';
      await this.loadThread();
      notify(
        this.translate.instant('supportTickets.toast.messageSent'),
        'info',
        2500
      );
    } catch {
      notify(
        this.translate.instant('supportTickets.toast.messageSendFailed'),
        'error',
        4000
      );
    } finally {
      this.postingComment = false;
    }
  }

  statusLabel(status: SupportTicketStatus): string {
    return this.translate.instant(`supportTickets.status.${status}`);
  }

  formatDate(d: Date | string | undefined): string {
    if (d == null) return '';
    const x = typeof d === 'string' ? new Date(d) : d;
    if (Number.isNaN(x.getTime())) return '';
    const locale = this.translate.currentLang?.startsWith('fr')
      ? 'fr-CA'
      : 'en-CA';
    return x.toLocaleString(locale);
  }

  private async loadThread(): Promise<void> {
    const ticketId = this.ticket._id;
    this.messagesLoading = true;
    this.messageThread = [];
    try {
      const res = await this.supportTicketService.listMessages(ticketId);
      if (this.closed) {
        return;
      }
      this.messageThread = res.items;
      try {
        await this.supportTicketService.markRead(
          ticketId,
          normalizeSupportTicketReporterLocale(this.translate.currentLang)
        );
        this.ticket.unreadMessageCount = 0;
      } catch {
        /* keep badge if mark-read failed */
      }
    } catch {
      if (!this.closed) {
        notify(
          this.translate.instant('supportTickets.toast.messagesLoadFailed'),
          'error',
          4000
        );
      }
    } finally {
      this.messagesLoading = false;
    }
  }
}
